import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { useState, useRef } from "react"
import csvModel from "@/assets/Planilha_Importacao_Giants_Modelo.csv"
import xlsxModel from "@/assets/Planilha_Importacao_Giants_Modelo.xlsx"
import { BatchProcessingResponse, BatchResultItem, BatchSummary } from "@/types/membros"
import { createBatchUsers } from "@/modules/Users/service/UsersService"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { useToast } from "@/contexts/ToastContext"

const TAMANHO_MAXIMO = 10 * 1024 * 1024
const EXTENSOES_PERMITIDAS = [".csv", ".xlsx", ".xls"]
const MIMES_PERMITIDOS = [
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]
const FORMATOS_TEXTO = EXTENSOES_PERMITIDAS.join(", ")

function extensaoDe(nome: string) {
  const ponto = nome.lastIndexOf(".")
  return ponto === -1 ? "" : nome.slice(ponto).toLowerCase()
}

const Stepper = ({ currentStep }: { currentStep: number }) => {
  const steps = ["Download do modelo", "Upload do arquivo", "Processamento"]
  return (
    <div className="mb-4 flex items-center justify-around sm:justify-between">
      {steps.map((step, index) => (
        <div
          key={index}
          className="flex flex-col items-center sm:flex-row"
        >
          <div
            className={`flex size-[50px] items-center justify-center rounded-full text-white ${index + 1 === currentStep ? "border border-primary text-primary" : "vorder-primary/60 border text-primary/60"}`}
          >
            {index + 1}
          </div>
          <p
            className={`ml-2 text-center text-xs leading-4 sm:text-left sm:text-[.9rem] ${index + 1 === currentStep ? "text-primary" : "text-gray-500"}`}
          >
            {step}
          </p>
        </div>
      ))}
    </div>
  )
}

export function BatchUploadModal({ onClose }: { onClose: () => void }) {
  const [currentStep, setCurrentStep] = useState(1)
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [responseSummary, setResponseSummary] = useState<BatchSummary | null>(null)
  const [responseResults, setResponseResults] = useState<BatchResultItem[] | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const { user } = useAuth()
  const toast = useToast()

  const selectFile = (selected: File) => {
    if (isProcessing) return
    const formatoValido =
      MIMES_PERMITIDOS.includes(selected.type) ||
      EXTENSOES_PERMITIDAS.includes(extensaoDe(selected.name))
    if (!formatoValido) {
      setFile(null)
      toast.danger(`Formato inválido. Envie um arquivo ${FORMATOS_TEXTO}.`)
      return
    }
    if (selected.size > TAMANHO_MAXIMO) {
      setFile(null)
      toast.danger(`Arquivo maior que ${TAMANHO_MAXIMO / (1024 * 1024)} MB.`)
      return
    }
    setFile(selected)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      selectFile(e.target.files[0])
    }
    // Sem isso, escolher o mesmo arquivo de novo (ex.: corrigido e salvo com o mesmo nome) não dispara onChange.
    e.target.value = ""
  }

  const handleSave = async () => {
    if (!file || isProcessing) return
    if (!user) {
      toast.danger("Sessão expirada. Faça login novamente.")
      return
    }

    const formData = new FormData()
    formData.append("file", file)
    formData.append("emp_id", String(user.empId))
    formData.append("originador_id", String(user.pesId))

    setIsProcessing(true)
    try {
      const { summary, results } = await createBatchUsers(formData)
      setResponseSummary(summary)
      setResponseResults(results)
      nextStep()
    } catch (error) {
      console.error(error)
      toast.danger(
        error?.response?.data?.message || "Não foi possível processar o arquivo. Tente novamente."
      )
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    if (isProcessing) return
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      selectFile(e.dataTransfer.files[0])
    }
  }

  const handleAreaClick = () => {
    if (isProcessing) return
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const nextStep = () => setCurrentStep((step) => step + 1)
  const prevStep = () => setCurrentStep((step) => step - 1)

  // Fechar durante o envio perderia o resumo do processamento, que continua no servidor.
  const handleOpenChange = (open: boolean) => {
    if (!open && !isProcessing) onClose()
  }

  const progress = (currentStep / 3) * 100 + "%"

  return (
    <Dialog
      open={true}
      onOpenChange={handleOpenChange}
    >
      <DialogContent className="max-w-[400px] overflow-hidden md:!max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Cadastro em Lote</DialogTitle>
        </DialogHeader>

        <div className="h-2.5 w-full rounded-full bg-gray-600 dark:bg-gray-700">
          <div
            className="h-2.5 rounded-full bg-white"
            style={{ width: progress }}
          />
        </div>

        <Stepper currentStep={currentStep} />
        <div className="grid gap-4 py-4">
          {currentStep === 1 && (
            <div className="grid gap-2">
              <div className="flex gap-2">
                <a
                  className="w-full"
                  href={csvModel}
                  download="giants_modelo_importacao_em_lote.csv"
                >
                  <Button
                    className="w-full"
                    variant="outline"
                  >
                    Baixar modelo CSV
                  </Button>
                </a>
                <a
                  className="w-full"
                  href={xlsxModel}
                  download="giants_modelo_importacao_em_lote.xlsx"
                >
                  <Button
                    className="w-full"
                    variant="outline"
                  >
                    Baixar modelo XLSX
                  </Button>
                </a>
              </div>

              <hr className="border-dark my-5 w-full" />

              <h6>Atenção ao preparar o seu arquivo para importação:</h6>
              <ul className="mx-5 list-outside list-disc text-[.8rem] italic text-white/50">
                <li>A ordem das colunas deve ser mantida;</li>
                <li>Sexo pode ser preenchido com: Masculino, Feminino, M e F;</li>
                <li>Perfil pode ser preenchido com: Colaborador, Liderança ou Convidado;</li>
                <li>
                  Telefones devem conter o DDD e podem ter caracteres especiais. Em caso de números
                  internacionais, deve se começar com "+" e o código do pais;
                </li>
                <li>E-mails devem ser válidos.</li>
              </ul>
            </div>
          )}
          {currentStep === 2 && (
            <div className="grid gap-2">
              <Label htmlFor="file-upload">Carregamento de modelo</Label>
              <div
                className={`cursor-pointer rounded-md border-2 border-dashed p-6 text-center ${isDragging ? "border-primary" : "border-border"}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={handleAreaClick}
              >
                <Input
                  id="file-upload"
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  accept={[...EXTENSOES_PERMITIDAS, ...MIMES_PERMITIDOS].join(",")}
                />
                {file ? (
                  <p>{file.name}</p>
                ) : (
                  <div>
                    <i className="fi fi-ts-document-circle-arrow-up text-[50px]"></i>
                    <p>
                      Arraste e solte o arquivo aqui ou clique para selecionar <br />{" "}
                      <small className="text-white/50">
                        Formatos permitidos: {FORMATOS_TEXTO} - Tamanho máximo: {TAMANHO_MAXIMO / (1024 * 1024)} MB
                      </small>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          {currentStep === 3 &&
            (responseSummary && responseResults ? (
              <div>
                <div className="mb-4 text-center">
                  <h3 className="text-lg font-semibold">Processamento Concluído</h3>
                  <div className="flex justify-center">
                    <div className="rounded-bl-full rounded-tl-full bg-slate-900 px-3 py-1">
                      <p className="text-sm text-slate-400">
                        Total de linhas: {responseSummary.totalRows}
                      </p>
                    </div>
                    <div className="bg-green-950/50 px-3 py-1">
                      <p className="text-sm text-green-500">
                        Sucessos: {responseSummary.successCount}
                      </p>
                    </div>
                    <div className="rounded-br-full rounded-tr-full bg-red-950/50 px-3 py-1">
                      <p className="text-sm text-red-500">Erros: {responseSummary.errorCount}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {responseSummary.successCount > 0 && (
                    <div className="rounded-sm border p-2">
                      <h3 className="font-semibold text-green-500">
                        Sucessos ({responseSummary.successCount})
                      </h3>
                      <ul className="mt-2 max-h-40 overflow-y-auto">
                        {responseResults
                          .filter((r) => r.status === "success")
                          .map((result, index) => (
                            <li
                              key={index}
                              className="mb-1 border-b border-gray-700 pb-1 text-sm text-gray-400"
                            >
                              <div>
                                <h6>
                                  {result.linha != null && (
                                    <span className="text-gray-500">Linha {result.linha} · </span>
                                  )}
                                  {result.row[1]}
                                </h6>
                                <p>{result.row[0]}</p>
                              </div>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                  {responseSummary.errorCount > 0 && (
                    <div className="rounded-sm border p-2">
                      <h3 className="font-semibold text-red-500">
                        Erros ({responseSummary.errorCount})
                      </h3>
                      <ul className="mt-2 max-h-60 overflow-y-auto">
                        {responseResults
                          .filter((r) => r.status === "error")
                          .map((result, index) => (
                            <li
                              key={index}
                              className="mb-1 border-b border-gray-700 pb-1 text-sm text-red-400"
                            >
                              <div>
                                <h6>
                                  {result.linha != null && (
                                    <span className="text-red-300">Linha {result.linha} · </span>
                                  )}
                                  {result.row[1] || "(sem nome)"}
                                </h6>
                                <p>{result.row[0]}</p>
                              </div>
                              <strong>Motivo: </strong>
                              {result.reason}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center">
                <p>Nenhum resultado para exibir.</p>
              </div>
            ))}
        </div>
        <DialogFooter>
          {currentStep > 1 && !responseResults && (
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
              disabled={isProcessing}
            >
              Voltar
            </Button>
          )}
          {currentStep < 2 ? (
            <Button
              type="button"
              onClick={nextStep}
              disabled={currentStep === 2 && !file}
            >
              Próximo
            </Button>
          ) : !responseResults ? (
            <Button
              type="submit"
              onClick={handleSave}
              disabled={!file || isProcessing}
            >
              {isProcessing ? "Processando..." : "Processar Arquivo"}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={onClose}
            >
              Fechar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
